import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import { Person as PersonIcon, Email as EmailIcon } from '@mui/icons-material';
import { makeApiCall } from '../../config/api';

const SalesPersonAutocomplete = ({ 
  value, 
  onChange, 
  name, 
  label, 
  required = false,
  disabled = false,
  sx = {}
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  // Search for sales persons
  const searchSalesPersons = async (searchTerm) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        search: searchTerm,
        limit: 20
      });
      
      const response = await makeApiCall(`api/customers/lookups/sales-persons/search?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setOptions(data.sales_persons || []);
      }
    } catch (error) {
      console.error('Error searching sales persons:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load initial options when component mounts
  useEffect(() => {
    searchSalesPersons('');
  }, []);

  // Search when input value changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue.length >= 0) { // Search even with empty string to get recent options
        searchSalesPersons(inputValue);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  // Handle selection change
  const handleChange = (event, newValue) => {
    // Create synthetic event for form compatibility
    const syntheticEvent = {
      target: {
        name: name,
        value: newValue ? newValue.id : ''
      }
    };
    onChange(syntheticEvent);
  };

  // Find selected option from current value
  const selectedOption = options.find(option => option.id === value) || null;

  return (
    <Autocomplete
      value={selectedOption}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      options={options}
      getOptionLabel={(option) => option.label || option.display_name || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      loadingText="Searching sales persons..."
      noOptionsText="No sales persons found"
      sx={{ ...sx }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <PersonIcon color="primary" fontSize="small" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {option.display_name || `${option.first_name} ${option.last_name}`}
              </Typography>
              {option.email && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon fontSize="inherit" />
                  {option.email}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            variant="outlined"
            label={option.display_name || option.label}
            {...getTagProps({ index })}
            key={option.id}
          />
        ))
      }
    />
  );
};

export default SalesPersonAutocomplete; 